import { useCallback, useEffect } from 'react';
import NsContent from 'components/common/ns-content/NsContent';
import capitalise from 'lodash/capitalize';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { ConsumptionText } from './components/ConsumptionText';
import { ConsumptionActualForm } from './components/ConsumptionActualForm';
import { useNavigation } from 'hooks/useNavigation';
import { useConsumptionEntry } from './hooks/useConsumptionEntry';
import { CONSUMPTION_TYPES } from 'config/app-config';

import {
  setConsumptionType,
  initialState
} from 'state/consumption-entry/consumption-entry-slice';
import {
  setConsumption,
  setInitialState as resetConsumption
} from 'state/consumption/consumption-slice';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';

import { useEnergyUsage } from 'hooks/useEnergyUsage';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { usePartner } from 'components/app/routes/usePartner';

export const testId = 'consumptionEntryTestId';

const CONSUMPTION_TYPE_OPTIONS = [
  {
    label: capitalise(CONSUMPTION_TYPES.LOW),
    value: CONSUMPTION_TYPES.LOW
  },
  {
    label: capitalise(CONSUMPTION_TYPES.MEDIUM),
    value: CONSUMPTION_TYPES.MEDIUM
  },
  {
    label: capitalise(CONSUMPTION_TYPES.HIGH),
    value: CONSUMPTION_TYPES.HIGH
  }
];

export const ConsumptionEntry = () => {
  const dispatch = useDispatch();
  const selectedFuelType = useSelector(selectFuelType, shallowEqual);
  const getEnergyUsage = useEnergyUsage();
  const {
    consumptionType,
    isMoveInJourney,
    isActual,
    isEnergyUsageExceeds
  } = useConsumptionEntry();

  const { experimentDefaultConsumptionType } = useFlags();
  const { isDSJourney } = usePartner();

  const defaultConsumptionType =
    isDSJourney && experimentDefaultConsumptionType
      ? CONSUMPTION_TYPES.ACTUAL
      : CONSUMPTION_TYPES.MEDIUM;

  const { eventTypes, next } = useNavigation();

  const handleChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      dispatch(setConsumptionType(value));
      if (value === CONSUMPTION_TYPES.ACTUAL) {
        dispatch(resetConsumption());
      } else {
        const consolidatedUsage = getEnergyUsage(value, selectedFuelType);
        dispatch(setConsumption(consolidatedUsage));
      }
    },
    [dispatch, getEnergyUsage, selectedFuelType]
  );

  const handleGoNext = useCallback(async () => {
    next({ eventType: eventTypes.NEXT });
  }, [next, eventTypes.NEXT]);

  useEffect(() => {
    if (consumptionType === initialState.data.consumptionType) {
      handleChange({ target: { value: defaultConsumptionType } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NsPanel testId={testId}>
      <NsBackCta />
      <NsContent>
        <NsTitle title="How much energy do you use a year?" />
      </NsContent>
      <NsForm onSubmit={handleGoNext}>
        <NsRadioInput
          className="inline"
          helper="Select the option that best fits your estimated energy usage."
          name="consumptionEntry"
          dataTestId="radioConsumptionEntrTestId"
          onChange={handleChange}
          value={consumptionType || defaultConsumptionType}
          options={[
            ...CONSUMPTION_TYPE_OPTIONS,
            ...(isMoveInJourney
              ? []
              : [
                  {
                    label: capitalise(CONSUMPTION_TYPES.ACTUAL),
                    value: CONSUMPTION_TYPES.ACTUAL
                  }
                ])
          ]}
        />
        <NsContent>
          <div>
            {!isActual && (
              <ConsumptionText
                consumptionType={consumptionType || CONSUMPTION_TYPES.MEDIUM}
                isMoveInJourney={isMoveInJourney}
              />
            )}
          </div>
        </NsContent>
        {isActual && (
          <ConsumptionActualForm isEnergyUsageExceeds={isEnergyUsageExceeds} />
        )}
        <NsPrimaryCta>
          {isEnergyUsageExceeds && <>I&apos;m happy to continue</>}
          {!isEnergyUsageExceeds && <>Continue</>}
        </NsPrimaryCta>
      </NsForm>
    </NsPanel>
  );
};

export default ConsumptionEntry;
